/**
 * `LocalConnection`: WS / DO を介さない、 ブラウザ内完結の対戦ランタイム。
 *
 * - DO の振る舞い (`MatchDO`) を TS で最小再現
 * - 状態は WASM の `matchOpen` / `matchApplyMove` / `matchClockSnapshot` に任せる
 * - 自分の手番が回ったとき以外、 CPU の手は `cpuPickMove` で自動消化
 * - 受信メッセージは `Connection.onMessage` 経由で useRoom と同じ ServerMsg として配信
 *
 * 対戦の永続化 (リロード復帰) は localStorage に snapshot を保存することで行う。
 */

import { type BridgeNtState, type BridgeSide, bridgePositions } from '../games/bridge-nt';
import type {
  BoardId,
  ClientMsg,
  MatchConfig,
  Participant,
  Role,
  ServerMsg,
} from '../proto';
import {
  type GameId,
  type MatchApplyResult,
  type MatchState,
  matchApplyBoardAction,
  matchApplyMove,
  matchClockSnapshot,
  matchOpenWithSeats,
} from '../wasm';
import { bgRollDice, type CpuLevel, ensureCpuWasm, pickMove } from '../wasm/cpu';
import type { Connection, ConnectionHandlers } from './connection';
import { canonicalLocalPokerMove, type LocalPokerHandCache, localPokerCacheFromState, projectLocalPokerSnapshot, projectLocalPokerState, restoreLocalPokerCache } from './local-poker-state';
import { matchOutcomeToRole, sideToRole } from './match-snapshot';
import { localMpsContext, localMpsNeedsRestart, mpsStorageKey, partialContext, pokerBoardTargets, pokerProvidePositions, shuffleContext } from './mps-context';
import { newGameFinishReason } from './new-game-finish-reason';
import type { PartialShare } from './poker-crypto';
import {
  cardIndexToRankSuit,
  combinePks,
  finalizeCard,
  initialDeck,
  type KeyPair,
  keypairFromSeed,
  partialDecryptWithProof,
  shuffleDeckWithProof,
  verifyPartial,
  verifyShuffle,
} from './poker-crypto';
import { awaitIdle } from './ui-busy';

export interface LocalSetup {
  /** 対戦 ID (URL の `:id` 部分)。 localStorage キーにも使う。 */
  id: string;
  /** プレイ構成 (template / games / clock / orchestrator)。 */
  config: MatchConfig;
  /** CPU の難易度。 */
  cpuLevel: CpuLevel;
  /** 自分の Role 選好。 'random' なら server と同じく公平にランダム配分。 */
  yourRolePreference: 'random' | 'host' | 'guest';
  /** 表示名 (自分 / CPU)。 */
  yourName: string;
  cpuName: string;
}

interface LocalSnapshot {
  state: MatchState;
  config: MatchConfig;
  /** ローカル対戦では 1 人プレイヤー (CPU は擬似 Role::Guest)。 */
  yourRole: Role;
  /** 終局フラグ (再 finished 防止)。 */
  finished: boolean;
  mpsVersion?: number;
  outcome?: 'host' | 'guest' | 'draw';
  finishReason?: string;
}

const STORAGE_PREFIX = 'mixed-game.local.';

export function loadLocalSnapshot(id: string): LocalSnapshot | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + id);
    if (!raw) return null;
    return JSON.parse(raw) as LocalSnapshot;
  } catch {
    return null;
  }
}

function saveLocalSnapshot(id: string, s: LocalSnapshot): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + id, JSON.stringify(s));
  } catch {
    // QuotaExceeded 等は黙殺 (対戦継続は可能)
  }
}

export function startLocalConnection(setup: LocalSetup, h: ConnectionHandlers): Connection {
  // 既存の保存があれば復元、 無ければ新規 open。
  let snap = loadLocalSnapshot(setup.id);
  if (!snap) {
    const now = Date.now();
    // Rust 側で host_seat / first_seats の公平抽選 + initial turn mask 再計算を一括実行。
    // ローカル対戦では「自分」 は常に Role::host (= player 本人)、 CPU は Role::guest。
    // setup.yourRolePreference は host_seat の決定方法だけ変える:
    //   'host'   → host_seat=Leader (= 自分が全体先手)
    //   'guest'  → host_seat=Follower (= CPU が全体先手)
    //   'random' → 50/50 (Rust 側 getrandom)
    const state = matchOpenWithSeats(setup.config, now, setup.yourRolePreference);
    snap = {
      state,
      config: setup.config,
      yourRole: 'host',
      finished: false,
      mpsVersion: 2,
    };
    saveLocalSnapshot(setup.id, snap);
  }

  const legacyMps = localMpsNeedsRestart(snap as Parameters<typeof localMpsNeedsRestart>[0]);
  let closed = false;
  const post = (msg: ServerMsg) => {
    if (closed) return;
    // Bridge の内部状態は4手札を持つ。 UI/storeへ渡すのは別のコピーだけ。
    if (msg.t === 'state') {
      for (const [key, context] of mpsContexts) {
        const [board, hand] = key.split(':').map(Number);
        if (currentMpsContext(board, hand, context)) h.onMessage({t: 'mps_context', board, hand, context});
      }
      void publishLocalContexts();
      try { h.onMessage({ ...msg, snapshot: projectLocalPokerSnapshot(bridgeSnapshotForPlayer(msg.snapshot), snap!.yourRole) }); }
      catch { h.onMessage({t: 'error', reason: '保存対局の表示データを確認できません'}); }
    } else if (msg.t === 'move_applied') {
      try {
        const game = localBoards().find(entry => entry.board === msg.board)?.game_id;
        const move = game === 'BridgeNT' ? redactBridgeMove(msg.mv) : game === 'HeadsUpPoker' ? canonicalLocalPokerMove(msg.mv) : msg.mv;
        h.onMessage({ ...msg, mv: move });
      } catch { h.onMessage({t: 'error', reason: '配牌通知を確認できません'}); }
    } else {
      h.onMessage(msg);
    }
  };


  // CPU を担当する Role (= ローカル対戦のプレイヤー本人ではない方)
  const cpuRole: Role = snap.yourRole === 'host' ? 'guest' : 'host';

  // CPU 用 wasm を warm up (失敗しても続行: random の Math.random fallback あり)
  void ensureCpuWasm().catch(() => {});

  type Seat = 'Leader' | 'Follower';
  const seatForRole = (role: Role): Seat | null => {
    if (role === 'spectator') return null;
    const hostSeat = (snap!.state as { host_seat?: Seat }).host_seat ?? 'Leader';
    return role === 'host'
      ? hostSeat
      : hostSeat === 'Leader'
        ? 'Follower'
        : 'Leader';
  };

  type LocalBoard = {
    board: BoardId;
    game_id: string;
    first_seat?: Seat;
    state: unknown;
    history?: { side?: BridgeSide; mv: unknown }[];
    result?: unknown;
  };
  const localBoards = (state: MatchState = snap!.state): LocalBoard[] =>
    (state as { boards?: LocalBoard[] }).boards ?? [];
  const isBridgeBoard = (board: BoardId): boolean =>
    localBoards().some(entry => entry.board === board && entry.game_id === 'BridgeNT');
  const bridgeSideForRole = (board: BoardId, role: Role): BridgeSide | null => {
    const entry = localBoards().find(b => b.board === board && b.game_id === 'BridgeNT');
    const seat = seatForRole(role);
    return entry && seat ? (seat === (entry.first_seat ?? 'Leader') ? 'First' : 'Second') : null;
  };
  const redactBridgeMove = (move: unknown): unknown => {
    if (move != null && typeof move === 'object' && 'provide_hands' in move) {
      return { provide_hands: { hands: [[], []] } };
    }
    return move;
  };
  const bridgeSnapshotForPlayer = (state: MatchState): MatchState => {
    if (!localBoards(state).some(b => b.game_id === 'BridgeNT')) return state;
    const copy = structuredClone(state);
    for (const entry of localBoards(copy)) {
      if (entry.game_id !== 'BridgeNT') continue;
      const view = entry.state as BridgeNtState;
      const playerSide = bridgeSideForRole(entry.board, snap!.yourRole);
      if (view.phase !== 'complete') {
        view.hands = view.hands.map((hand, seat) =>
          playerSide != null && seat % 2 === (playerSide === 'First' ? 0 : 1) ? hand : null,
        ) as BridgeNtState['hands'];
      }
      // プレイ中の配牌historyは伏せる。全52枚を出した終局後だけ、公開済み手札で
      // 提供履歴を再構成して棋譜再生を可能にする。復号share/proofは常に除去。
      if (entry.history) {
        for (const item of entry.history) {
          if (view.phase === 'complete' && item.side && item.mv != null && typeof item.mv === 'object' && 'provide_hands' in item.mv) {
            const first = item.side === 'First' ? 0 : 1;
            item.mv = { provide_hands: { hands: [view.hands[first] ?? [], view.hands[first + 2] ?? []] } };
          } else {
            item.mv = redactBridgeMove(item.mv);
          }
        }
      }
    }
    return copy;
  };

  /** board が CPU の指せる board に含まれるか (= サーバ派生 `_view.allowed_boards` を直読)。
   *  旧 m.turn ベースの判定は撤廃。 新仕様では「指せる board」 は唯一の派生計算結果。 */
  const isCpuTurnOnBoard = (board: { board: BoardId }): boolean => {
    const cpuSeat = seatForRole(cpuRole);
    if (!cpuSeat) return false;
    const view =
      (snap!.state as { _view?: { allowed_boards?: Record<string, number[]> } })._view;
    const allowed = view?.allowed_boards?.[cpuSeat] ?? [];
    return allowed.includes(board.board);
  };

  // 初期 broadcast の前に、 Backgammon の dice を振り直す (空配列のままだと
  // CPU/Player が EndTurn しかできなくて詰む)。
  if (!legacyMps) { rollEmptyBackgammonDice(snap); saveLocalSnapshot(setup.id, snap); }

  const mpsContexts = new Map<string, string>();
  const contextPending = new Map<string, Promise<void>>();
  const currentHand = (board: BoardId): number | null => {
    const entry = localBoards().find(candidate => candidate.board === board);
    if (entry?.game_id === 'BridgeNT') return 1;
    if (entry?.game_id !== 'HeadsUpPoker') return null;
    return (entry.state as {current_hand?: {hand_number?: number}}).current_hand?.hand_number ?? null;
  };
  const currentMpsContext = (board: BoardId, hand: number, context: string) => !closed && !legacyMps && !snap!.finished && currentHand(board) === hand && localBoards().find(entry => entry.board === board)?.result == null && mpsContexts.get(`${board}:${hand}`) === context;
  const publishLocalContexts = async () => {
    if (closed || legacyMps || snap!.finished) return;
    for (const entry of localBoards()) {
      const hand = currentHand(entry.board);
      if (hand == null) continue;
      const key = `${entry.board}:${hand}`;
      if (mpsContexts.has(key)) continue;
      let pending = contextPending.get(key);
      if (!pending) {
        pending = (async () => {
          const context = await localMpsContext(setup.id, entry.board, hand, entry.game_id, seatForRole('host') ?? 'Leader', entry.first_seat ?? 'Leader');
          if (closed || snap!.finished || currentHand(entry.board) !== hand) return;
          mpsContexts.set(key, context);
          post({t: 'mps_context', board: entry.board, hand, context});
        })().catch(() => { if (!closed) post({t: 'error', reason: '配牌contextを準備できません'}); });
        contextPending.set(key, pending);
      }
      await pending;
      if (contextPending.get(key) === pending) contextPending.delete(key);
    }
  };

  const broadcastInitial = () => {
    if (!snap) return;
    const participants: Participant[] = [
      { sub: 'local-you', display_name: setup.yourName, role: 'host' },
      { sub: 'local-cpu', display_name: setup.cpuName, role: 'guest' },
    ];
    post({
      t: 'lobby_state',
      participants,
      config: setup.config,
      you: snap.yourRole,
    });
    post({ t: 'started' });
    post({
      t: 'state',
      snapshot: snap.state,
    });
    post({
      t: 'clock_tick',
      clock: matchClockSnapshot(snap.state, setup.config, Date.now()),
    });
    if (snap.finished && snap.outcome) {
      post({
        t: 'finished',
        outcome: snap.outcome,
        archive_key: null,
        reason: snap.finishReason ?? 'resign',
      });
    }
  };

  // 自分の手番でない (= CPU が打つべき) なら CPU を 1 手進める。
  // 連鎖して打つので while ループ。 安全のため最大 100 手で抜ける。
  //
  // **並走ガード** (`stepCpuRunning`): ユーザーが指すたびに `stepCpu` が起動するが、
  // 前回起動分が `pickMove` (Worker 応答待ち) で await 中だと並走累積し、 着手を
  // 重ねるほど Worker postMessage の queue が膨らんで体感が遅くなる (= 報告された
  // 「指してるとだんだん反応が悪くなる」 の原因)。 同時に 1 つだけ走ればよいので
  // 単純な mutex ガードで abandoned-call は単に飛ばす。
  // ===== Mental Poker (= docs §8.10) CPU 側 mock =====
  //
  // ===== Mental Poker CPU 側鍵管理 (= Golle 経路、 docs §8.10.X 共通) =====
  //
  // Player (= host) と CPU (= guest) の 2 者間 mental poker を local 完結。 DO の
  // relay (= crates/mixed-do/src/lib.rs) と等価な振る舞いを TS で再現する基盤:
  //   - MpsKeyShare (player) → CPU keypair 生成 → MpsKeysReady broadcast
  //   - Golle on-demand pick の per-slot commit / share は下記 handleCpuMpsGolle* で
  interface CpuMpsKeyState {
    keypair: KeyPair | null;
    playerPublicKeyHex: string | null;
  }
  const cpuMps = new Map<string, CpuMpsKeyState>();
  const cpuMpsKey = (board: BoardId, hand: number) => `${board}:${hand}:${mpsContexts.get(`${board}:${hand}`) ?? 'unavailable'}`;
  const getOrInitCpuMps = (board: BoardId, hand: number): CpuMpsKeyState => {
    const key = cpuMpsKey(board, hand);
    let entry = cpuMps.get(key);
    if (!entry) { entry = {keypair: null, playerPublicKeyHex: null}; cpuMps.set(key, entry); }
    return entry;
  };
  const hexToBytes = (hex: string): Uint8Array => Uint8Array.from(hex.match(/../g) ?? [], byte => Number.parseInt(byte, 16));
  const bytesToHex = (bytes: Uint8Array): string => Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  const randomHex32 = () => bytesToHex(crypto.getRandomValues(new Uint8Array(32)));
  const ensureCpuKeypair = async (board: BoardId, hand: number, context: string): Promise<KeyPair> => {
    const key = mpsStorageKey('seed', setup.id, board, hand, context, 'guest');
    let seed: string | null = null;
    try { seed = localStorage.getItem(key); } catch { /* memory only */ }
    if (!seed || !/^[0-9a-fA-F]{64}$/.test(seed)) {
      seed = randomHex32();
      try { localStorage.setItem(key, seed); } catch { /* current deal continues */ }
    }
    return keypairFromSeed(seed);
  };
  const ensureCpuShuffleNonces = (board: BoardId, hand: number, context: string): {perm: string; rerand: string; proof: string} => {
    const key = mpsStorageKey('shuffle', setup.id, board, hand, context, 'guest');
    try {
      const parsed = JSON.parse(localStorage.getItem(key) ?? 'null');
      if (parsed && [parsed.perm, parsed.rerand, parsed.proof].every(value => typeof value === 'string' && /^[0-9a-fA-F]{64}$/.test(value))) return parsed;
    } catch { /* fresh nonces */ }
    const nonces = {perm: randomHex32(), rerand: randomHex32(), proof: randomHex32()};
    try { localStorage.setItem(key, JSON.stringify(nonces)); } catch { /* current deal continues */ }
    return nonces;
  };
  const handleCpuMpsKeyShare = async (board: BoardId, hand: number, context: string, playerPk: Uint8Array) => {
    if (!currentMpsContext(board, hand, context) || playerPk.length !== 32) return;
    const entry = getOrInitCpuMps(board, hand);
    const playerHex = bytesToHex(playerPk);
    if (entry.playerPublicKeyHex && entry.playerPublicKeyHex !== playerHex) return;
    entry.playerPublicKeyHex = playerHex;
    try {
      const keypair = entry.keypair ?? await ensureCpuKeypair(board, hand, context);
      if (!currentMpsContext(board, hand, context)) return;
      entry.keypair = keypair;
      post({t: 'mps_keys_ready', board, hand, context, host_public_key: Array.from(playerPk), guest_public_key: Array.from(hexToBytes(keypair.pk))});
    } catch { post({t: 'error', reason: '配牌の鍵を準備できません'}); }
  };

  const cpuResolvedCard = new Map<string, Map<number, number>>();
  const cpuFinalDeck = new Map<string, string[]>();
  const acceptedShuffles = new Map<string, {deck: string[]; proof: string; finalDeck: string[]; cpuProof: string}>();
  const shufflePending = new Map<string, Promise<void>>();
  const verifiedPlayerPartials = new Map<string, Map<number, PartialShare>>();
  const cpuPartials = new Map<string, Map<number, PartialShare>>();
  const setCpuResolved = (board: BoardId, hand: number, position: number, card: number) => {
    const key = cpuMpsKey(board, hand);
    const cards = cpuResolvedCard.get(key) ?? new Map<number, number>();
    cards.set(position, card);
    cpuResolvedCard.set(key, cards);
  };
  const getCpuResolvedHoleCards = (board: BoardId, hand: number, cpuIsSb: boolean): [Card, Card] | null => {
    const cards = cpuResolvedCard.get(cpuMpsKey(board, hand));
    const positions = cpuIsSb ? [0, 1] : [2, 3];
    const first = cards?.get(positions[0]);
    const second = cards?.get(positions[1]);
    return first == null || second == null ? null : [cardIndexToRankSuit(first), cardIndexToRankSuit(second)];
  };
  const getCpuResolvedBoardCards = (board: BoardId, hand: number): Card[] => {
    const cards = cpuResolvedCard.get(cpuMpsKey(board, hand));
    const out: Card[] = [];
    for (const position of [4, 5, 6, 7, 8]) { const card = cards?.get(position); if (card == null) break; out.push(cardIndexToRankSuit(card)); }
    return out;
  };
  const getCpuBridgeHands = (board: BoardId): [Card[], Card[]] | null => {
    const side = bridgeSideForRole(board, cpuRole);
    if (!side) return null;
    const stored = cpuResolvedCard.get(cpuMpsKey(board, 1));
    const indices = bridgePositions(side).map(position => stored?.get(position));
    if (indices.some(index => index == null)) return null;
    const cards = indices.map(index => cardIndexToRankSuit(index!));
    return [cards.slice(0, 13), cards.slice(13)];
  };
  const cpuIsSbForHand = (board: BoardId, hand: number): boolean => {
    const entry = localBoards().find(candidate => candidate.board === board);
    const poker = entry?.state as {current_dealer_side?: BridgeSide; current_hand?: {hand_number?: number}} | undefined;
    if (!entry || poker?.current_hand?.hand_number !== hand) return false;
    const playerSide = seatForRole(snap!.yourRole) === (entry.first_seat ?? 'Leader') ? 'First' : 'Second';
    return playerSide !== (poker.current_dealer_side ?? 'First');
  };
  const cpuPartial = async (board: BoardId, hand: number, context: string, position: number): Promise<PartialShare | null> => {
    if (!currentMpsContext(board, hand, context)) return null;
    const key = cpuMpsKey(board, hand);
    const cache = cpuPartials.get(key) ?? new Map<number, PartialShare>();
    const previous = cache.get(position);
    if (previous) return previous;
    const entry = getOrInitCpuMps(board, hand);
    const deck = cpuFinalDeck.get(key);
    if (!entry.keypair || !deck) return null;
    const partial = await partialDecryptWithProof(deck[position], entry.keypair.sk, partialContext(context, 'guest', position));
    if (!currentMpsContext(board, hand, context)) return null;
    cache.set(position, partial);
    cpuPartials.set(key, cache);
    return partial;
  };
  const sendCpuHolePartial = async (board: BoardId, hand: number, context: string) => {
    const side = bridgeSideForRole(board, 'host');
    const positions = side ? bridgePositions(side) : cpuIsSbForHand(board, hand) ? [2, 3] : [0, 1];
    const partials: PartialShare[] = [];
    for (const position of positions) { const partial = await cpuPartial(board, hand, context, position); if (!partial) return; partials.push(partial); }
    if (!currentMpsContext(board, hand, context)) return;
    post({t: 'mps_partial_decrypt_ready', board, hand, context, from: 'guest', card_indices: positions, shares: partials.map(partial => partial.share), proofs: partials.map(partial => partial.proof)});
  };
  const handleCpuMpsShuffle = async (board: BoardId, hand: number, context: string, deck: string[], proof: string) => {
    if (!currentMpsContext(board, hand, context) || deck.length !== 52 || deck.some(card => !/^[0-9a-fA-F]{128}$/.test(card)) || typeof proof !== 'string') return;
    const input = [...deck];
    const key = cpuMpsKey(board, hand);
    const pending = (shufflePending.get(key) ?? Promise.resolve()).then(async () => {
      if (!currentMpsContext(board, hand, context)) return;
      const entry = getOrInitCpuMps(board, hand);
      if (!entry.keypair || !entry.playerPublicKeyHex) return;
      let accepted = acceptedShuffles.get(key);
      if (accepted && (accepted.proof !== proof || accepted.deck.some((card, index) => card !== input[index]))) return;
      if (!accepted) {
        const combined = await combinePks([entry.playerPublicKeyHex, entry.keypair.pk]);
        if (!currentMpsContext(board, hand, context)) return;
        const initial = await initialDeck(combined, context);
        if (!currentMpsContext(board, hand, context)) return;
        const valid = await verifyShuffle(initial, input, combined, proof, shuffleContext(context, 'host'));
        if (!currentMpsContext(board, hand, context) || !valid) return;
        const nonces = ensureCpuShuffleNonces(board, hand, context);
        const shuffled = await shuffleDeckWithProof(input, combined, nonces.perm, nonces.rerand, nonces.proof, shuffleContext(context, 'guest'));
        if (!currentMpsContext(board, hand, context)) return;
        accepted = {deck: input, proof, finalDeck: shuffled.deck, cpuProof: shuffled.proof};
        acceptedShuffles.set(key, accepted);
        cpuFinalDeck.set(key, accepted.finalDeck);
      }
      post({t: 'mps_shuffle_ready', board, hand, context, deck: [...accepted.deck], round: 1, proof: accepted.proof});
      post({t: 'mps_shuffle_ready', board, hand, context, deck: [...accepted.finalDeck], round: 2, proof: accepted.cpuProof});
      await sendCpuHolePartial(board, hand, context);
    }).catch(() => { if (currentMpsContext(board, hand, context)) post({t: 'error', reason: '配牌のシャッフルを確認できません'}); });
    shufflePending.set(key, pending);
    await pending;
    if (shufflePending.get(key) === pending) shufflePending.delete(key);
  };
  const pokerPublicPositions = (board: BoardId): number[] => {
    const state = localBoards().find(entry => entry.board === board)?.state as {current_hand?: {board?: Card[]; awaiting_cards?: string | null}} | undefined;
    const hand = state?.current_hand;
    return [...new Set([...[4, 5, 6, 7, 8].slice(0, hand?.board?.length ?? 0), ...pokerBoardTargets(hand?.awaiting_cards ?? null)])];
  };
  const handleCpuMpsPartialDecrypt = async (board: BoardId, hand: number, context: string, positions: number[], shares: string[], proofs: string[]) => {
    if (!currentMpsContext(board, hand, context) || positions.length !== shares.length || positions.length !== proofs.length || new Set(positions).size !== positions.length) return;
    const entry = getOrInitCpuMps(board, hand);
    const key = cpuMpsKey(board, hand);
    const deck = cpuFinalDeck.get(key);
    if (!entry.keypair || !entry.playerPublicKeyHex || !deck) return;
    const side = bridgeSideForRole(board, cpuRole);
    const ownPositions = side ? bridgePositions(side) : cpuIsSbForHand(board, hand) ? [0, 1] : [2, 3];
    const publicPositions = side ? [] : pokerPublicPositions(board);
    if (!side) {
      const publicBoard = (localBoards().find(candidate => candidate.board === board)?.state as {current_hand?: {board?: Card[]}} | undefined)?.current_hand?.board ?? [];
      publicBoard.forEach((card, index) => { setCpuResolved(board, hand, 4 + index, (card.rank - 2) * 4 + card.suit); });
    }
    if (side && (positions.length !== 26 || positions.some(position => !ownPositions.includes(position)))) return;
    if (positions.some(position => !ownPositions.includes(position) && !publicPositions.includes(position))) return;
    const decoded: [number, number][] = [];
    const publicPartials: [number, PartialShare][] = [];
    const validated: [number, PartialShare][] = [];
    try {
      for (const [index, position] of positions.entries()) {
        const valid = await verifyPartial(deck[position], entry.playerPublicKeyHex, shares[index], proofs[index], partialContext(context, 'host', position));
        if (!currentMpsContext(board, hand, context) || !valid) return;
        const own = await cpuPartial(board, hand, context, position);
        if (!own) return;
        const card = await finalizeCard(deck[position], [own.share, shares[index]]);
        if (!currentMpsContext(board, hand, context) || card == null) return;
        decoded.push([position, card]);
        validated.push([position, {share: shares[index], proof: proofs[index]}]);
        if (publicPositions.includes(position)) publicPartials.push([position, own]);
      }
    } catch { return; }
    if (!currentMpsContext(board, hand, context)) return;
    const cache = verifiedPlayerPartials.get(key) ?? new Map<number, PartialShare>();
    for (const [position, partial] of validated) cache.set(position, partial);
    verifiedPlayerPartials.set(key, cache);
    for (const [position, card] of decoded) setCpuResolved(board, hand, position, card);
    if (publicPartials.length) post({t: 'mps_partial_decrypt_ready', board, hand, context, from: 'guest', card_indices: publicPartials.map(([position]) => position), shares: publicPartials.map(([, partial]) => partial.share), proofs: publicPartials.map(([, partial]) => partial.proof)});
    // No echo of host private shares into the recipient's relay cache.
    if (side) {
      const bridge = localBoards().find(candidate => candidate.board === board)?.state as BridgeNtState | undefined;
      if (bridge?.provided[side === 'First' ? 0 : 1]) { const hands = getCpuBridgeHands(board); if (hands) captureBridgeProvide(board, side, hands); }
    }
    void stepCpu();
  };

  let stepCpuRunning = false;
  let stepCpuRequested = false;
  const stepCpu = async () => {
    if (closed || !snap || snap.finished || legacyMps) return;
    if (stepCpuRunning) { stepCpuRequested = true; return; }
    stepCpuRunning = true;
    try {
      await stepCpuInner();
    } finally {
      stepCpuRunning = false;
      if (stepCpuRequested) { stepCpuRequested = false; void stepCpu(); }
    }
  };
  const stepCpuInner = async () => {
    let safety = 100;
    while (safety-- > 0) {
      const boards = (snap.state as { boards?: { board: BoardId; game_id: string; state: unknown; current_side: 'First' | 'Second' | null; result: 'First' | 'Second' | 'Draw' | null }[] }).boards ?? [];
      // §8.10.A: poker の hole/board が未確定で wait しても他盤の CPU 着手は止めない。
      // 旧 `boards.find(...)` は最初の CPU 手番盤に固定して、 そこが wait なら全 stepCpu を
      // 抜けていた (= 並列モードで poker が他盤の CPU を巻き添えにスタックさせていた)。
      // 全候補を順に試し、 1 つでも apply できたら state 変化のため while で再評価する。
      const candidates = boards.filter((b) => b.result === null && isCpuTurnOnBoard(b));
      if (candidates.length === 0) return;
      let progressed = false;
      for (const cpuBoard of candidates) {
      // pickMove は Web Worker で走るので main thread を block しないが、 worker から
      // 応答が返るまでの「沈黙」 が長すぎると「すぐ打ってくる CPU」 になって対局感が薄い。
      // 体感を保つため最小遅延 (200ms / BG sub-move 100ms) と UI アニメ完了待ち
      // (= awaitIdle) を両方並列で待ってから pick リクエストを送る。
      //
      // 非アニメ盤 (Chess/Renju/...) は awaitIdle が raf x2 後に即 resolve するので
      // 実質 minMs 待つだけ。
      //
      // 例外: BG で 「次の手が EndTurn 確定」 (= dice 空) の局面は思考要素ゼロなので
      // sleep もアニメ待ちも不要 (= ダイスを振り直すだけ)。
      const bgDice = cpuBoard.game_id === 'Backgammon'
        ? (cpuBoard.state as { dice?: number[] }).dice
        : undefined;
      const isBgPendingEndTurn =
        cpuBoard.game_id === 'Backgammon' && Array.isArray(bgDice) && bgDice.length === 0;
      const minDelayMs = isBgPendingEndTurn
        ? 0
        : cpuBoard.game_id === 'Backgammon'
          ? 100
          : 200;
      if (minDelayMs > 0) {
        await Promise.all([sleep(minDelayMs), awaitIdle()]);
        if (closed || snap.finished) return;
      } else {
        // EndTurn 確定でも awaitIdle だけは挟む (= 直前 sub-move のスライドアニメ完了待ち)
        await awaitIdle();
        if (closed || snap.finished) return;
      }
      // HU Poker の ProvideMyHole 段階は pickMove に任せず、 Golle protocol で復元した
      // CPU の real hole を直接 provide する。 pickMove 経由だと engine legal が返す
      // DUMMY card template (= [2♠,2♠]) がそのまま提供され、 後続の preflop で
      // PokerSearcher が duplicate card で None を返して CPU 着手 stuck になる
      // (= e2e 完走 flaky の原因)。 復元 hole が未確定 (= protocol まだ完走してない)
      // なら DUMMY fallback (= 旧挙動)、 確定したら real cards で provide。
      let mv: unknown;
      if (cpuBoard.game_id === 'BridgeNT' && (cpuBoard.state as BridgeNtState).phase === 'awaiting_hands') {
        const hands = getCpuBridgeHands(cpuBoard.board);
        if (!hands) continue;
        mv = { provide_hands: { hands } };
      } else if (cpuBoard.game_id === 'HeadsUpPoker') {
        const st = cpuBoard.state as {
          current_hand?: {
            hand_number?: number;
            awaiting_cards?: string;
            to_act?: 'sb' | 'bb';
            board?: unknown[];
          };
        };
        const awaiting = st.current_hand?.awaiting_cards;
        const handN = st.current_hand?.hand_number ?? 1;
        const toAct = st.current_hand?.to_act ?? 'sb';
        if (awaiting === 'holes') {
          const cpuIsSb = cpuIsSbForHand(cpuBoard.board, handN);
          // 自分の番でないとそもそも provide できない
          if ((cpuIsSb && toAct === 'sb') || (!cpuIsSb && toAct === 'bb')) {
            const real = getCpuResolvedHoleCards(cpuBoard.board, handN, cpuIsSb);
            if (real) {
              mv = { provide_my_hole: { seat: cpuIsSb ? 'sb' : 'bb', cards: real } };
            } else {
              // shuffle protocol で CPU の hole がまだ finalize できていない。 DUMMY を渡すと
              // PokerSearcher が duplicate card で None 返却する flaky 原因になるので 1 手
              // wait する (= 次の MpsPartialDecrypt 受信時に stepCpu 再 trigger で再評価)。
              // §8.10.A: この wait は当該 board 単位、 他盤候補 (chess 等) は試行継続する
              // ため continue で次へ。 旧実装の return は他盤の CPU を巻き添えにしていた。
              continue;
            }
          }
        } else if (
          awaiting === 'flop' ||
          awaiting === 'turn' ||
          awaiting === 'river' ||
          awaiting === 'all_in_run_out'
        ) {
          // board 公開: CPU が finalize した real board card を provide。 player 側 useEffect も
          // 同時に provide するが engine は最初の 1 つを受理 (2 つ目は applyMove で reject)。
          const boardLen = Array.isArray(st.current_hand?.board)
            ? st.current_hand.board.length
            : 0;
          const need = awaiting === 'flop' ? 3 : awaiting === 'all_in_run_out' ? 5 - boardLen : 1;
          const resolved = getCpuResolvedBoardCards(cpuBoard.board, handN);
          const slice = resolved.slice(boardLen, boardLen + need);
          if (need > 0 && slice.length === need) {
            mv = { provide_board_cards: { cards: slice } };
          } else {
            // board card がまだ finalize できていない → 当該 board は wait、 他盤試行継続。
            continue;
          }
        }
      }
      if (mv === undefined) {
        try {
          let cpuView = cpuBoard.state;
          if (cpuBoard.game_id === 'HeadsUpPoker') {
            restoreUnmaskedToSnap();
            const entry = localBoards().find(candidate => candidate.board === cpuBoard.board);
            const seat = seatForRole(cpuRole);
            const side = seat == null ? null : seat === (entry?.first_seat ?? 'Leader') ? 'First' : 'Second';
            cpuView = projectLocalPokerState(entry?.state ?? cpuBoard.state, side);
          }
          if (cpuBoard.game_id === 'BridgeNT') {
            restoreBridgeUnmaskedToSnap();
            const side = bridgeSideForRole(cpuBoard.board, cpuRole);
            const bridge = cpuBoard.state as BridgeNtState;
            // CPUは自分の2手札と出た札だけを知る。相手の未知札はRust側がsampleする。
            cpuView = {
              ...bridge,
              hands: bridge.hands.map((hand, seat) => side && seat % 2 === (side === 'First' ? 0 : 1) ? hand : null),
            };
          }
          mv = await pickMove(
          cpuBoard.game_id as GameId,
          cpuView,
          cpuBoard.current_side as 'First' | 'Second',
          setup.cpuLevel,
        );
        } catch (e) {
          console.error('CPU pickMove failed (worker)', e);
          return;
        }
      }
      // worker 応答待ちの間に対局が閉じた / 終局した可能性を再確認
      if (closed || !snap || snap.finished) return;
      if (mv == null) {
        // Backgammon の dice 待ちなど。 ローカル対戦では Backgammon の dice は
        // engine 内部で振られない (VRF 依存) ので、 簡易にここで振る:
        // → 現状は engine 側で 'end_turn' を含む合法手は出るので null は稀。
        return;
      }
      try {
        const cpuSeat = seatForRole(cpuRole);
        if (!cpuSeat) return;
        applyMove(cpuBoard.board, cpuSeat, mv);
        // §8.10.A: 1 盤 apply 成功 → state 変化したので while で再評価 (= 残候補は次 round で)。
        progressed = true;
        break;
      } catch (e) {
        console.error('CPU move apply failed', e);
        return;
      }
      } // end for (cpuBoard of candidates)
      if (!progressed) return; // 全候補 wait → stepCpu 抜ける (= 次の re-trigger を待つ)
    }
  };

  // HU Poker 専用「未マスク state」 保持 (= matchApplyMove の戻り state は
  // spectator-mask で hole/board が null 化されており、 次の applyMove に渡すと
  // engine が「両 hole まだ」 と誤認して awaiting=Holes に固着する。 player+CPU が
  // ProvideMyHole/ProvideBoardCards で送った実値を per-(board,hand) で track し、
  // matchApplyMove の戻り state に再注入することで unmask 状態を維持する。)
  type Card = { rank: number; suit: number };
  const bridgeUnmasked = new Map<BoardId, BridgeNtState['hands']>();
  // 保存されたローカル内部状態から復元。投稿されるsnapshotには相手手札を含めない。
  for (const entry of localBoards()) {
    if (entry.game_id === 'BridgeNT') {
      const hands = (entry.state as BridgeNtState).hands;
      bridgeUnmasked.set(entry.board, hands.map(hand => hand ? hand.map(card => ({ ...card })) : null) as BridgeNtState['hands']);
    }
  }
  const captureBridgeProvide = (board: BoardId, side: BridgeSide, hands: [Card[], Card[]]) => {
    const known = bridgeUnmasked.get(board) ?? [null, null, null, null];
    const first = side === 'First' ? 0 : 1;
    known[first] = hands[0].map(card => ({ ...card }));
    known[first + 2] = hands[1].map(card => ({ ...card }));
    bridgeUnmasked.set(board, known);
  };
  const restoreBridgeUnmaskedToSnap = () => {
    for (const entry of localBoards()) {
      if (entry.game_id !== 'BridgeNT') continue;
      const hands = bridgeUnmasked.get(entry.board);
      if (!hands) continue;
      const bridge = entry.state as BridgeNtState;
      bridge.hands = hands.map(hand => hand ? hand.map(card => ({ ...card })) : null) as BridgeNtState['hands'];
      if (entry.history) for (const item of entry.history) item.mv = redactBridgeMove(item.mv);
    }
  };
  const isValidBridgeHand = (hand: unknown): hand is Card[] => Array.isArray(hand) && hand.length === 13 && hand.every(card =>
    card != null && typeof card === 'object' && Number.isInteger(card.rank) && card.rank >= 2 && card.rank <= 14 && Number.isInteger(card.suit) && card.suit >= 0 && card.suit <= 3,
  ) && new Set(hand.map(card => `${card.rank}:${card.suit}`)).size === 13;

  /** 配牌申告は平文を信用せず、自分の26枚のDLEQと最終deckから照合する。 */
  const verifyPlayerBridgeProvide = async (board: BoardId, move: unknown): Promise<unknown> => {
    const body = (move as { provide_hands?: { hands?: unknown[]; reveal?: { context?: string; shares?: unknown[]; proofs?: unknown[] } } }).provide_hands;
    const side = bridgeSideForRole(board, snap!.yourRole);
    const entry = localBoards().find(b => b.board === board);
    const state = entry?.state as BridgeNtState | undefined;
    const cpuS = getOrInitCpuMps(board, 1);
    const deck = cpuFinalDeck.get(cpuMpsKey(board, 1));
    if (!side || !state || state.phase !== 'awaiting_hands' || state.provided[side === 'First' ? 0 : 1] || !cpuS.keypair || !cpuS.playerPublicKeyHex || !deck || deck.length !== 52) throw new Error('Bridge配牌の検証準備ができていません');
    if (!body || !Array.isArray(body.hands) || body.hands.length !== 2 || !body.hands.every(isValidBridgeHand)) throw new Error('Bridge配牌は各席13枚で指定してください');
    const context = mpsContexts.get(`${board}:1`);
    if (!context || body.reveal?.context !== context || !currentMpsContext(board, 1, context)) throw new Error('Bridge配牌contextが一致しません');
    const shares = body.reveal?.shares;
    const proofs = body.reveal?.proofs;
    if (!Array.isArray(shares) || shares.length !== 26 || !shares.every(share => typeof share === 'string') || !Array.isArray(proofs) || proofs.length !== 26 || !proofs.every(proof => typeof proof === 'string')) throw new Error('Bridge配牌の26枚分の復号証明が必要です');
    const decoded: Card[] = [];
    for (const [i, position] of bridgePositions(side).entries()) {
      if (!await verifyPartial(deck[position], cpuS.playerPublicKeyHex, shares[i] as string, proofs[i] as string, partialContext(context, 'host', position))) throw new Error('Bridge配牌の復号証明が一致しません');
      if (!currentMpsContext(board, 1, context)) throw new Error('配牌contextが変わりました');
      const own = await cpuPartial(board, 1, context, position);
      if (!own) throw new Error('配牌の復号準備ができていません');
      const share = own.share;
      const card = await finalizeCard(deck[position], [share, shares[i] as string]);
      if (card == null) throw new Error('Bridge配牌を復号できません');
      if (!currentMpsContext(board, 1, context)) throw new Error('配牌contextが変わりました');
      decoded.push(cardIndexToRankSuit(card));
    }
    const cardSetKey = (cards: Card[]) => cards.map(card => `${card.rank}:${card.suit}`).sort().join(',');
    const hands = body.hands as [Card[], Card[]];
    if (hands.some((hand, i) => cardSetKey(hand) !== cardSetKey(decoded.slice(i * 13, i * 13 + 13)))) throw new Error('Bridge配牌がシャッフル結果と一致しません');
    // revealはengine/history/broadcastへ入れず、検証済み26枚だけをapplyする。
    const latestBridge = localBoards().find(candidate => candidate.board === board)?.state as BridgeNtState | undefined;
    if (!currentMpsContext(board, 1, context) || latestBridge?.phase !== 'awaiting_hands' || latestBridge.provided[side === 'First' ? 0 : 1]) throw new Error('Bridge配牌の局面が変わりました');
    return { provide_hands: { hands: [decoded.slice(0, 13), decoded.slice(13)] } };
  };
  const verifyPlayerPokerProvide = async (board: BoardId, move: unknown): Promise<unknown> => {
    const entry = localBoards().find(candidate => candidate.board === board && candidate.game_id === 'HeadsUpPoker');
    const poker = entry?.state as {current_hand?: {hand_number: number; awaiting_cards: string | null; board: Card[]}} | undefined;
    const hand = poker?.current_hand;
    if (!hand) throw new Error('Poker配牌の局面がありません');
    const handNumber = hand.hand_number;
    const context = mpsContexts.get(`${board}:${handNumber}`);
    const body = move as {provide_my_hole?: {seat?: string; cards?: Card[]; reveal?: {context?: string; shares?: string[]; proofs?: string[]}}; provide_board_cards?: {cards?: Card[]; reveal?: {context?: string; shares?: string[]; proofs?: string[]}}};
    const supplied = body.provide_my_hole ?? body.provide_board_cards;
    if (!supplied || !context || supplied.reveal?.context !== context || !currentMpsContext(board, handNumber, context)) throw new Error('Poker配牌contextが一致しません');
    const mySeat = cpuIsSbForHand(board, handNumber) ? 'bb' : 'sb';
    const positions = body.provide_my_hole ? mySeat === 'sb' ? [0, 1] : [2, 3] : pokerProvidePositions(hand.awaiting_cards, hand.board.length);
    if (body.provide_my_hole && (hand.awaiting_cards !== 'holes' || body.provide_my_hole.seat !== mySeat)) throw new Error('Poker配牌の席または段階が一致しません');
    const reveal = supplied.reveal;
    if (!positions.length || !Array.isArray(supplied.cards) || supplied.cards.length !== positions.length || !Array.isArray(reveal.shares) || reveal.shares.length !== positions.length || !Array.isArray(reveal.proofs) || reveal.proofs.length !== positions.length) throw new Error('Poker配牌の復号証明が不足しています');
    const key = cpuMpsKey(board, handNumber);
    const cpu = getOrInitCpuMps(board, handNumber);
    const deck = cpuFinalDeck.get(key);
    if (!cpu.playerPublicKeyHex || !deck) throw new Error('Poker配牌の検証準備ができていません');
    const cards: Card[] = [];
    for (const [index, position] of positions.entries()) {
      const valid = await verifyPartial(deck[position], cpu.playerPublicKeyHex, reveal.shares[index], reveal.proofs[index], partialContext(context, 'host', position));
      if (!currentMpsContext(board, handNumber, context) || !valid) throw new Error('Poker配牌の復号証明が一致しません');
      const other = await cpuPartial(board, handNumber, context, position);
      if (!other) throw new Error('Poker配牌の復号準備ができていません');
      // A public card also requires the opposite share to have been requested at this street.
      if (!body.provide_my_hole && !verifiedPlayerPartials.get(key)?.has(position)) throw new Error('Poker公開札の復号交換が未完了です');
      const indexValue = await finalizeCard(deck[position], [other.share, reveal.shares[index]]);
      if (!currentMpsContext(board, handNumber, context) || indexValue == null) throw new Error('Poker配牌を復号できません');
      const card = cardIndexToRankSuit(indexValue);
      if (card.rank !== supplied.cards[index]?.rank || card.suit !== supplied.cards[index]?.suit) throw new Error('Poker配牌がシャッフル結果と一致しません');
      cards.push(card);
    }
    const latest = localBoards().find(candidate => candidate.board === board)?.state as typeof poker;
    if (latest?.current_hand?.hand_number !== handNumber || latest.current_hand.awaiting_cards !== hand.awaiting_cards || latest.current_hand.board.length !== hand.board.length) throw new Error('Poker配牌の局面が変わりました');
    // Recipient proofs never enter engine history or move_applied.
    return body.provide_my_hole ? {provide_my_hole: {seat: mySeat, cards}} : {provide_board_cards: {cards}};
  };
  const huUnmasked = new Map<BoardId, LocalPokerHandCache>();
  const huKey = (board: BoardId) => board;
  // Hydrate both internal private hands before any first post-reload Call/Check.
  for (const entry of localBoards()) if (entry.game_id === 'HeadsUpPoker') {
    huUnmasked.set(entry.board, localPokerCacheFromState(entry.state));
  }
  const captureProvideMove = (board: BoardId, mv: unknown) => {
    if (!snap) return;
    const m = mv as {
      provide_my_hole?: { seat: 'sb' | 'bb'; cards: [Card, Card] };
      provide_board_cards?: { cards: Card[] };
    };
    const boards = (snap.state as { boards?: { board: BoardId; game_id: string; state?: { current_hand?: { hand_number?: number } } }[] }).boards ?? [];
    const bs = boards.find((b) => b.board === board);
    if (!bs || bs.game_id !== 'HeadsUpPoker') return;
    const handN = bs.state?.current_hand?.hand_number ?? 1;
    let s = huUnmasked.get(huKey(board));
    if (!s || s.hand_number !== handN) {
      s = { hand_number: handN, sbHole: null, bbHole: null, board: [] };
      huUnmasked.set(huKey(board), s);
    }
    if (m.provide_my_hole) {
      const { seat, cards } = m.provide_my_hole;
      if (seat === 'sb') s.sbHole = [cards[0], cards[1]];
      else s.bbHole = [cards[0], cards[1]];
    }
    if (m.provide_board_cards) {
      for (const c of m.provide_board_cards.cards) s.board.push(c);
    }
  };
  const restoreUnmaskedToSnap = () => {
    if (!snap) return;
    for (const entry of localBoards()) {
      if (entry.game_id !== 'HeadsUpPoker') continue;
      const cached = huUnmasked.get(huKey(entry.board));
      if (cached) restoreLocalPokerCache(entry.state, cached);
    }
  };

  const applyMove = (
    board: BoardId,
    seat: Seat,
    mv: unknown,
    clientId?: string,
  ): void => {
    if (!snap) return;
    // 戻り state は spectator-mask されるので、 直前に既知の unmask 値を snap.state に
    // 再注入してから engine に渡す。 (これをしないと「両 hole 未提供」 と engine が
    // 誤認して provide フェーズが永久ループする)
    restoreUnmaskedToSnap();
    restoreBridgeUnmaskedToSnap();
    let result: MatchApplyResult;
    try {
      result = matchApplyMove(snap.state, setup.config, board, seat, mv, Date.now());
    } catch (e) {
      if (clientId) {
        post({ t: 'move_rejected', client_id: clientId, reason: String(e) });
      }
      return;
    }
    // ProvideMyHole / ProvideBoardCards の値を per-(board, hand) track。
    captureProvideMove(board, mv);
    const bridgeEntry = localBoards().find(entry => entry.board === board && entry.game_id === 'BridgeNT');
    if (bridgeEntry && mv != null && typeof mv === 'object' && 'provide_hands' in mv) {
      const hands = (mv as { provide_hands: { hands: [Card[], Card[]] } }).provide_hands.hands;
      captureBridgeProvide(board, seat === (bridgeEntry.first_seat ?? 'Leader') ? 'First' : 'Second', hands);
    }
    snap.state = result.state;
    // restore directly into the new (masked) state so subsequent reads see unmask
    restoreUnmaskedToSnap();
    restoreBridgeUnmaskedToSnap();
    // Backgammon: 「EndTurn 完了で side が反転した直後」 に限り dice を振り直す。
    // (= ターン開始時に新規ロール) 全 dice 消費 直後の dice=[] 状態で振り直すと
    // 「同じ side のターンがダイス復活で続く」 という壊れた状態になるので、
    // 必ず side flip を検出してから振る。
    if (isBackgammonEndTurn(mv)) {
      rollEmptyBackgammonDice(snap);
    }
    saveLocalSnapshot(setup.id, snap);
    post({
      t: 'move_applied',
      board,
      mv,
      clock: result.clock_snapshot,
    });
    post({
      t: 'state',
      snapshot: snap.state,
    });
    // CPU 戦の MatchConfig は auto_finish=false で起こしているので、 engine は
    // state.outcome を立てない (= 途中で validate_move に block されない)。 その代わり
    // 勝敗判定 (WinFormula::MajorityOfBoards) の結果は result.match_outcome に毎回
    // 返ってくるので、 対人戦 (server 経路) と同じく **過半数が確定した時点で** finish する。
    // 3 盤中 2 勝で終わらず 3 盤目を指し続けさせるのは誤り (対人戦とも食い違う)。
    if (result.match_outcome) {
      const hostSeat = (snap.state as { host_seat?: Seat }).host_seat ?? 'Leader';
      const winnerRole = matchOutcomeToRole(result.match_outcome, hostSeat);
      const reason = gameFinishReason(snap, board) ?? 'win_by_score';
      finish(winnerRole, reason);
      return;
    }
    // フォールバック: WinFormula が Custom 等で engine が判定を返さない場合は、 全盤決着を
    // 見て host/guest の勝ち板数を集計する。
    const boardsAfter =
      (snap.state as {
        boards?: {
          first_seat?: 'Leader' | 'Follower';
          result: 'First' | 'Second' | 'Draw' | null;
        }[];
      }).boards ?? [];
    const allFinished = boardsAfter.length > 0 && boardsAfter.every((b) => b.result !== null);
    if (allFinished) {
      const hostSeat = (snap.state as { host_seat?: 'Leader' | 'Follower' }).host_seat ?? 'Leader';
      let hostWins = 0;
      let guestWins = 0;
      for (const b of boardsAfter) {
        if (b.result === 'Draw') continue;
        const fs: 'Leader' | 'Follower' = b.first_seat ?? 'Leader';
        const hostPlaysFirst = fs === hostSeat;
        const hostWonHere =
          (b.result === 'First' && hostPlaysFirst) || (b.result === 'Second' && !hostPlaysFirst);
        if (hostWonHere) hostWins++;
        else guestWins++;
      }
      const winnerRole: 'host' | 'guest' | 'draw' =
        hostWins > guestWins ? 'host' : guestWins > hostWins ? 'guest' : 'draw';
      const reason = gameFinishReason(snap, board) ?? 'win_by_score';
      finish(winnerRole, reason);
    }
  };

  const finish = (outcome: 'host' | 'guest' | 'draw', reason: string) => {
    if (!snap || snap.finished) return;
    snap.finished = true;
    snap.outcome = outcome;
    snap.finishReason = reason;
    saveLocalSnapshot(setup.id, snap);
    post({
      t: 'finished',
      outcome,
      archive_key: null,
      reason,
    });
  };

  // open 直後に LobbyState + State + ClockTick を同期で配信。
  // 旧実装は setTimeout(0) で遅延していたが、 React 18 StrictMode の二重 mount や
  // 早期 cleanup と組合わさると、 「準備中…」 表示のまま broadcast が来ない
  // ケースが起きていたため同期化した。 connectLocal 側で先に phase='connecting' を
  // set してから本関数を呼ぶ順序で、 ここでの phase='playing' 反映が確実になる。
  broadcastInitial();
  if (legacyMps) post({t: 'error', reason: 'この保存対局は旧配牌方式です。保存内容を残したまま新しい対局を開始してください'});
  h.onOpen?.();
  // CPU の手は即時起動 (内部で awaitIdle + 最小遅延 200ms 待ってから 1 手目を打つ)
  void stepCpu();

  return {
    send(msg: ClientMsg) {
      if (!snap || closed) return;
      if (legacyMps && msg.t !== 'hello') { post({t: 'error', reason: '旧配牌方式の保存対局は新しい対局で再開してください'}); return; }
      switch (msg.t) {
        case 'hello':
          // 再 broadcast (リロード時)
          broadcastInitial();
          break;
        case 'move': {
          // 自分は Role::host (local 対戦の player 本人)。 host_seat に従い MatchSeat を決定。
          const yourSeat = seatForRole(snap.yourRole);
          if (!yourSeat) return;
          if (isBridgeBoard(msg.board) && msg.mv != null && typeof msg.mv === 'object' && 'provide_hands' in msg.mv) {
            void verifyPlayerBridgeProvide(msg.board, msg.mv).then(clean => {
              if (closed || !snap || snap.finished) return;
              applyMove(msg.board, yourSeat, clean, msg.client_id);
              void stepCpu();
            }).catch(error => {
              post({ t: 'move_rejected', client_id: msg.client_id, reason: String(error) });
            });
            break;
          }
          const pokerBoard = localBoards().some(entry => entry.board === msg.board && entry.game_id === 'HeadsUpPoker');
          if (pokerBoard && msg.mv != null && typeof msg.mv === 'object' && ('provide_my_hole' in msg.mv || 'provide_board_cards' in msg.mv)) {
            void verifyPlayerPokerProvide(msg.board, msg.mv).then(clean => {
              if (closed || !snap || snap.finished) return;
              applyMove(msg.board, yourSeat, clean, msg.client_id);
              void stepCpu();
            }).catch(error => post({t: 'move_rejected', client_id: msg.client_id, reason: String(error)}));
            break;
          }
          applyMove(msg.board, yourSeat, msg.mv, msg.client_id);
          // 着手後、 残りボードの CPU 番を消化
          void stepCpu();
          break;
        }
        case 'resign':
          // 自分の投了 → CPU の勝ち
          finish(cpuRole, 'resign');
          break;
        case 'claim_timeout': {
          // クロックを再計算して flagged を判定。 flagged は (board, side) で、 side は per-board
          // Side ('First'|'Second' or 旧 'Host'|'Guest')。 当該 board の first_seat と host_seat
          // から、 flag を立てた側の Role を求める。
          const clk = matchClockSnapshot(snap.state, setup.config, Date.now()) as
            | { flagged?: [number, string] | null }
            | null;
          if (clk?.flagged) {
            const [boardId, side] = clk.flagged;
            const flaggedRole = sideToRole(snap.state, boardId, side);
            if (flaggedRole) {
              const winner: Role = flaggedRole === 'host' ? 'guest' : 'host';
              finish(winner, 'timeout');
            }
          }
          break;
        }
        case 'draw_offer':
          // ローカル CPU は draw 拒否 (即取り下げ)
          post({ t: 'draw_offered', from: snap.yourRole });
          setTimeout(() => post({ t: 'draw_withdrawn' }), 600);
          break;
        case 'board_action': {
          // 盤単位の Resign / OfferDraw / AcceptDraw / DeclineDraw。
          // 全部 engine の apply_board_action に委譲し、 OfferDraw だけ CPU 自動拒否を後追いで打つ。
          const yourSeat = seatForRole(snap.yourRole);
          if (!yourSeat) return;
          let result: MatchApplyResult;
          try {
            result = matchApplyBoardAction(
              snap.state,
              setup.config,
              msg.board,
              yourSeat,
              msg.action,
            );
          } catch (e) {
            console.error('board_action apply failed', e);
            return;
          }
          snap.state = result.state;
          saveLocalSnapshot(setup.id, snap);
          post({
            t: 'state',
            snapshot: snap.state,
          });
          if (result.match_outcome) {
            // match_outcome は MatchSeat 基準 (First = Leader)。 host_seat を渡さないと
            // 「CPU が Leader」 の対局で投了・引き分け合意の勝敗が反転する。
            const hostSeat = (snap.state as { host_seat?: Seat }).host_seat ?? 'Leader';
            const winnerRole = matchOutcomeToRole(result.match_outcome, hostSeat);
            const reason = msg.action === 'Resign' ? 'resign' : 'draw_agreed';
            finish(winnerRole, reason);
            break;
          }
          if (msg.action === 'OfferDraw') {
            const cpuSeat = seatForRole(cpuRole);
            if (cpuSeat) {
              setTimeout(() => {
                if (closed || !snap || snap.finished) return;
                try {
                  const r2 = matchApplyBoardAction(
                    snap.state,
                    setup.config,
                    msg.board,
                    cpuSeat,
                    'DeclineDraw',
                  );
                  snap.state = r2.state;
                  saveLocalSnapshot(setup.id, snap);
                  post({
                    t: 'state',
                    snapshot: snap.state,
                  });
                } catch (e) {
                  console.error('CPU auto-decline failed', e);
                }
              }, 600);
            }
          }
          // Resign が match を終わらせなかった (= 過半数未達) 場合や、 他盤に CPU
          // 手番が残っているケースがあるので stepCpu を回す。
          void stepCpu();
          break;
        }
        case 'mps_key_share':
          void handleCpuMpsKeyShare(msg.board, msg.hand, msg.context, Uint8Array.from(msg.public_key));
          break;
        case 'mps_shuffle':
          void handleCpuMpsShuffle(msg.board, msg.hand, msg.context, msg.deck, msg.proof);
          break;
        case 'mps_partial_decrypt':
          void handleCpuMpsPartialDecrypt(
            msg.board,
            msg.hand,
            msg.context,
            msg.card_indices,
            msg.shares,
            msg.proofs,
          );
          break;
        case 'draw_accept':
        case 'leave':
        case 'config_update':
        case 'start':
        case 'vrf_publish_key':
        case 'vrf_submit_proof':
          // ローカル対戦では未使用。 silent ignore。
          break;
      }
    },
    close() {
      closed = true;
      h.onClose?.(true);
    },
    isReconnecting() {
      return false;
    },
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** 終局した board の game_id と state.outcome から game-specific な finish reason を返す。
 * server 経路 (mixed-engine::engines::board_finish_reason) と揃え、 UI 表示も統一する。
 * 旧 "win_by_score" にフォールバックする呼び出し側を想定。 */
function gameFinishReason(snap: LocalSnapshot, board: BoardId): string | null {
  const boards = (snap.state as { boards?: { board: BoardId; game_id: string; state: unknown }[] } | null)
    ?.boards;
  const entry = boards?.find((b) => b.board === board);
  if (!entry) return null;
  const state = entry.state as
    | { outcome?: ({ reason?: string; how?: string; end_kind?: string; kind?: string; winner?: unknown } | string) | null }
    | null;
  const outcome = state?.outcome;
  if (!outcome) return null;
  const newReason = newGameFinishReason(entry.game_id, entry.state);
  if (newReason) return newReason;
  switch (entry.game_id) {
    case 'Renju': {
      const r = typeof outcome === 'object' ? outcome.reason : undefined;
      return r === 'Five'
        ? 'renju_five'
        : r === 'Overline'
          ? 'renju_overline'
          : r === 'Forbidden'
            ? 'renju_forbidden'
            : null;
    }
    case 'Chess': {
      const r = typeof outcome === 'object' ? outcome.reason : undefined;
      switch (r) {
        case 'Checkmate':
          return 'chess_checkmate';
        case 'Stalemate':
          return 'chess_stalemate';
        case 'FiftyMove':
          return 'chess_fifty_move';
        case 'SeventyFiveMove':
          return 'chess_seventyfive_move';
        case 'Threefold':
          return 'chess_threefold';
        case 'Fivefold':
          return 'chess_fivefold';
        case 'InsufficientMaterial':
          return 'chess_insufficient_material';
        case 'Resignation':
          return 'resign';
        case 'Timeout':
          return 'timeout';
        case 'DrawAgreement':
          return 'draw_agreed';
        default:
          return null;
      }
    }
    case 'Shogi': {
      // ShogiOutcome は { Win: { winner, reason } } | { Draw: { reason } } の enum 形式 (serde tagging)
      const o = outcome as { Win?: { reason?: string }; Draw?: { reason?: string } };
      const winReason = o.Win?.reason;
      const drawReason = o.Draw?.reason;
      if (winReason === 'Checkmate') return 'shogi_checkmate';
      if (winReason === 'Try') return 'shogi_try';
      if (winReason === 'IllegalRepeat') return 'shogi_perpetual_check';
      if (winReason === 'Resign') return 'resign';
      if (winReason === 'Timeout') return 'timeout';
      if (drawReason === 'Sennichite') return 'shogi_sennichite';
      if (drawReason === 'DrawAgreement') return 'draw_agreed';
      return null;
    }
    case 'Jungo': {
      const endKind = typeof outcome === 'object' ? outcome.end_kind : undefined;
      return endKind === 'PassAliveMko'
        ? 'jungo_pass_alive'
        : endKind === 'TwoPasses'
          ? 'jungo_two_passes'
          : null;
    }
    case 'AnimalShogi': {
      const r = typeof outcome === 'object' ? outcome.reason : undefined;
      switch (r) {
        case 'Catch':
          return 'animal_catch';
        case 'Try':
          return 'animal_try';
        case 'Stalemate':
          return 'animal_stalemate';
        case 'Repetition':
          return 'animal_repetition';
        case 'MaxPlies':
          return 'animal_max_plies';
        default:
          return null;
      }
    }
    case 'Reversi':
    case 'Othello': {
      const winner = typeof outcome === 'object' ? outcome.winner : undefined;
      return winner == null ? 'reversi_draw' : 'reversi_win_by_score';
    }
    case 'Backgammon':
      return 'bg_bear_off';
    case 'TwixT': {
      const k = typeof outcome === 'object' ? outcome.kind : undefined;
      if (k === 'Connection') return 'twixt_connection';
      if (k === 'DrawTrivial') return 'twixt_draw_trivial';
      if (k === 'DrawAlgorithmic') return 'twixt_draw_algorithmic';
      return null;
    }
    case 'Xiangqi': {
      // XiangqiOutcome は { Win: { winner, reason } } | { Draw: { reason } } の enum 形式
      const o = outcome as { Win?: { reason?: string }; Draw?: { reason?: string } };
      const winReason = o.Win?.reason;
      const drawReason = o.Draw?.reason;
      if (winReason === 'Checkmate') return 'xiangqi_checkmate';
      if (winReason === 'Stalemate') return 'xiangqi_stalemate';
      if (winReason === 'PerpetualCheck') return 'xiangqi_perpetual_check';
      if (winReason === 'Resign') return 'resign';
      if (winReason === 'Timeout') return 'timeout';
      if (drawReason === 'SixtyMoveRule') return 'xiangqi_sixty_move';
      if (drawReason === 'Threefold') return 'xiangqi_threefold';
      if (drawReason === 'MutualPerpetualCheck') return 'xiangqi_mutual_perpetual_check';
      if (drawReason === 'DrawAgreement') return 'draw_agreed';
      return null;
    }
    case 'Janggi': {
      // JanggiOutcome は { kind: 'Win', reason } | { kind: 'Score', reason } の internally tagged enum。
      const o = outcome as
        | { kind?: string; reason?: string }
        | string;
      if (typeof o !== 'object') return null;
      if (o.kind === 'Win') {
        switch (o.reason) {
          case 'Checkmate':
            return 'janggi_checkmate';
          case 'Resign':
            return 'resign';
          case 'Timeout':
            return 'timeout';
          case 'Disconnect':
            return 'disconnect';
          default:
            return null;
        }
      }
      if (o.kind === 'Score') {
        switch (o.reason) {
          case 'BikjangConfirmed':
            return 'janggi_bikjang';
          case 'RepetitionThreshold':
            return 'janggi_repetition';
          case 'TwoPasses':
            return 'janggi_two_passes';
          case 'PlyCap':
            return 'janggi_ply_cap';
          default:
            return null;
        }
      }
      return null;
    }
    case 'HeadsUpPoker':
      return 'poker_bust_out';
    default:
      return null;
  }
}

interface BgBoard {
  board: BoardId;
  game_id: string;
  state: { dice?: number[] };
  result: 'First' | 'Second' | 'Draw' | null;
}

/** mv が Backgammon の `'end_turn'` リテラルかを判定 (= ターン送り)。 */
function isBackgammonEndTurn(mv: unknown): boolean {
  return mv === 'end_turn';
}

/** snapshot 内の Backgammon ボードで dice が空のものに、 ローカル擬似乱数で
 * 振り直す (`bgRollDice` を呼ぶ)。 進行中ボードのみ対象。 */
function rollEmptyBackgammonDice(snap: LocalSnapshot): void {
  const state = snap.state as { boards?: BgBoard[] } | null;
  if (!state?.boards) return;
  for (const b of state.boards) {
    if (b.game_id !== 'Backgammon') continue;
    if (b.result !== null) continue;
    const dice = b.state?.dice ?? [];
    if (dice.length > 0) continue;
    try {
      const newBoardState = bgRollDice(b.state);
      // mutate in place (state は MatchState の slot)
      b.state = newBoardState as { dice?: number[] };
    } catch (e) {
      console.error('bgRollDice failed', e);
    }
  }
}
